import React from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
} from '@amzn/awsui-components-console';

import {
  columnDisplayDndLabels,
  type TablePreferencesConfig,
  type TablePreferencesValue,
} from './tablePreferencesConfig';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';

/**
 * The preferences gear, shared by every table. Offers Cloudscape's standard set,
 * each wired to a Table prop by the consumer. Sticky columns is the one omission:
 * it needs a per-table option list, and these tables pin their first column.
 */

/**
 * Resource ids for the dialog copy. `title` is also the gear's only accessible name.
 *
 * Ids rather than text, named `*Id`, so a call site cannot render one as a label by
 * accident — resolve with `t()`.
 */
export const PREFERENCES_LABEL_IDS = {
  titleId: 'table-prefs-title',
  confirmLabelId: 'table-prefs-confirm',
  cancelLabelId: 'table-prefs-cancel',
  closeAriaLabelId: 'table-prefs-close-aria',
  pageSizeTitleId: 'table-prefs-page-size-title',
  wrapLinesLabelId: 'table-prefs-wrap-lines-label',
  wrapLinesDescriptionId: 'table-prefs-wrap-lines-desc',
  stripedRowsLabelId: 'table-prefs-striped-rows-label',
  stripedRowsDescriptionId: 'table-prefs-striped-rows-desc',
  contentDensityLabelId: 'table-prefs-density-label',
  contentDensityDescriptionId: 'table-prefs-density-desc',
  contentDisplayTitleId: 'table-prefs-content-display-title',
  contentDisplayDescriptionId: 'table-prefs-content-display-desc',
} as const;

interface TablePreferencesProps {
  readonly config: TablePreferencesConfig;
  readonly preferences: TablePreferencesValue;
  /** Takes the RAW payload; `useTablePreferences` normalises it. */
  readonly onConfirm: (
    preferences: CollectionPreferencesProps.Preferences,
  ) => void;
}

/** The page-size list. "N rows" unless `config.pageSizeLabel` overrides it. */
function pageSizeOptions(
  t: TranslateFn,
  config: TablePreferencesConfig,
): CollectionPreferencesProps.PageSizePreference['options'] | undefined {
  if (config.pageSizeOptions === undefined) return undefined;
  const label =
    config.pageSizeLabel ??
    ((translate: TranslateFn, value: number) =>
      translate('table-prefs-page-size-rows', { value }));
  return config.pageSizeOptions.map((value) => ({
    value,
    label: label(t, value),
  }));
}

const TablePreferences: React.FC<TablePreferencesProps> = ({
  config,
  preferences,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const sizeOptions = pageSizeOptions(t, config);

  return (
    <CollectionPreferences
      title={t(PREFERENCES_LABEL_IDS.titleId)}
      confirmLabel={t(PREFERENCES_LABEL_IDS.confirmLabelId)}
      cancelLabel={t(PREFERENCES_LABEL_IDS.cancelLabelId)}
      closeAriaLabel={t(PREFERENCES_LABEL_IDS.closeAriaLabelId)}
      preferences={preferences}
      onConfirm={({ detail }) => onConfirm(detail)}
      // Omitted for an unpaginated table rather than shown empty.
      pageSizePreference={
        sizeOptions && {
          title: t(PREFERENCES_LABEL_IDS.pageSizeTitleId),
          options: sizeOptions,
        }
      }
      wrapLinesPreference={{
        label: t(PREFERENCES_LABEL_IDS.wrapLinesLabelId),
        description: t(PREFERENCES_LABEL_IDS.wrapLinesDescriptionId),
      }}
      stripedRowsPreference={{
        label: t(PREFERENCES_LABEL_IDS.stripedRowsLabelId),
        description: t(PREFERENCES_LABEL_IDS.stripedRowsDescriptionId),
      }}
      contentDensityPreference={{
        label: t(PREFERENCES_LABEL_IDS.contentDensityLabelId),
        description: t(PREFERENCES_LABEL_IDS.contentDensityDescriptionId),
      }}
      contentDisplayPreference={{
        title: t(PREFERENCES_LABEL_IDS.contentDisplayTitleId),
        description: t(PREFERENCES_LABEL_IDS.contentDisplayDescriptionId),
        options: config.columnOptions,
        ...columnDisplayDndLabels(t),
      }}
    />
  );
};

export default TablePreferences;
